<template>
  <div>
    <div>
      <div class="page-title-cont">
        <h1 class="wp-heading-inline">
          WPCal.io - {{ T__("General Settings") }}
        </h1>
        <AdminHeaderRight />
      </div>
      <SettingsMenu />
      <div class="col-cont settings-cont">
        <div class="col">
          <div class="form">
            <div class="validation_errors_cont"></div>
            <div class="setting-group-title">{{ T__("Default settings") }}</div>
            <form-row :validator="$v.form.working_hours">
              <label>{{ T__("Default Working Hours") }}</label>
              <TimeRange1ToN v-model="working_hours" :disable_multiple="true" />
              <span class="txt-help">{{
                T__(
                  "Changing this will not affect existing event types but only newly-created event types."
                )
              }}</span>
            </form-row>
            <form-row :validator="$v.form.working_hours.from_time"></form-row>
            <form-row :validator="$v.form.working_hours.to_time"></form-row>

            <form-row :validator="$v.form.working_days">
              <label>{{ T__("Default Working Days") }}</label>
              <WeekDaysSelector v-model="form.working_days" />
              <span class="txt-help">{{
                T__(
                  "Changing this will not affect existing event types but only newly-created event types."
                )
              }}</span>
            </form-row>

            <form-row :validator="$v.form.timezone">
              <label>{{ T__("Default Time Zone") }}</label>
              <TimeZoneSelector v-model="form.timezone" :use_as="'form_tz'" />
              <span class="txt-help">{{
                T__(
                  "Changing this will not affect existing event types but only newly-created event types."
                )
              }}</span>
            </form-row>

            <form-row :validator="$v.form.time_format">
              <label>{{ T__("Time Format") }}</label>
              <ul class="selector inline time">
                <li>
                  <input
                    type="radio"
                    id="time-format-12h"
                    name="time-format"
                    value="12hrs"
                    v-model="form.time_format"
                    @blur="$v.form.time_format.$touch()"
                  /><label for="time-format-12h">
                    12 <span>{{ T_x("hr", "time hour") }}</span></label
                  >
                </li>
                <li>
                  <input
                    type="radio"
                    id="time-format-24h"
                    name="time-format"
                    value="24hrs"
                    v-model="form.time_format"
                    @blur="$v.form.time_format.$touch()"
                  /><label for="time-format-24h">
                    24 <span>{{ T_x("hr", "time hour") }}</span></label
                  >
                </li>
              </ul>
              <span class="txt-help">{{
                T__(
                  "This will be used for both admin end and for the invitee in the booking widget."
                )
              }}</span>
            </form-row>

            <hr />
            <div class="setting-group-title" style="display:flex;">
              {{ T__("Branding") }}
              <PremiumInfoBadge
                style="margin: -9px 0 0 10px;"
                highlight_feature="branding_customization"
              >
                <em>This feature is included<br />in the PRO plan.</em>
              </PremiumInfoBadge>
            </div>

            <form-row :validator="$v.form.branding_color">
              <label>{{ T__("Brand color") }}</label>
              <div style="display:flex">
                <input
                  type="text"
                  v-model="form.branding_color"
                  placeholder="#567bf3"
                  style="width: 90px;"
                  @keyup="may_prefix_with_hash"
                  @blur="$v.form.branding_color.$touch()"
                />
                <div
                  style="height: 40px; width: 40px; border-radius:5px; border: 1px solid #b1b6d1; margin-left:5px;"
                  :style="{ 'background-color': brand_color_or_default }"
                ></div>
                <div
                  style="height: 40px; width: 40px; border-radius:5px; border: 1px solid #b1b6d1; margin-left:5px; background-color:#fff; overflow: hidden;"
                >
                  <div
                    :style="{ 'background-color': brand_color_or_default }"
                    style="opacity:0.1; width:40px; height:40px;"
                  ></div>
                </div>
              </div>
              <span class="txt-help">{{
                T__(
                  "This color will be used as the accent color in the booking widget. We will automatically use a lighter shade as the background on certain elements."
                )
              }}</span>
            </form-row>

            <form-row :validator="$v.form.branding_font">
              <label>{{ T__("Brand font") }}</label>
              <label class="label-cb"
                ><input
                  type="checkbox"
                  true-value="inherit"
                  false-value=""
                  v-model="form.branding_font"
                />
                {{
                  T__("Use the website's default font for the booking widget.")
                }}</label
              >
            </form-row>

            <!-- <form-row :validator="$v.form.branding_font">
              <label>Remove WPCal.io branding</label>
              <label class="label-cb"
                ><input type="checkbox" /> Remove all WPCal.io branding from the
                booking widget.</label
              >
            </form-row> -->

            <hr />

            <form-row :validator="$v.form.use_wp_mail">
              <div class="setting-group-title" style="display:flex;">
                {{ T__("Email") }}
                <PremiumInfoBadge
                  style="margin: -9px 0 0 10px;"
                  highlight_feature="email_features"
                >
                  <em>This feature is included<br />in the PRO plan.</em>
                </PremiumInfoBadge>
              </div>
              <label>WP Mailer</label>
              <span class="txt-help" style="margin-bottom:5px;"
                >We send all notification emails through our servers for better
                deliverability. If you have set up your own reliable system, you
                can choose to use WP Mailer (optionally, along with an SMTP
                plugin) instead.</span
              >
              <label class="label-cb"
                ><input
                  type="checkbox"
                  true-value="1"
                  false-value="0"
                  v-model="form.use_wp_mail"
                  @change="may_alert_already_scheduled_mails"
                />
                {{ T__("I understand. Use WP Mailer instead.") }}</label
              >
            </form-row>
            <div class="wpc-form-row">
              <label
                >Email template &amp; Calendar event content customization in
                PHP</label
              >
              <span class="txt-help" style="margin-bottom:5px;">
                <a
                  href="https://help.wpcal.io/en/article/how-to-customise-email-templates-in-php-5369yp/?utm_source=wpcal_plugin&utm_medium=admin_header"
                  style="display:inline-block; margin-bottom:5px;"
                  >See how to customize &nearr;</a
                ><br />
                <a @click="load_and_show_template_overrides"
                  >See list of template overrides</a
                >
              </span>
            </div>
            <hr />
            <form-row :validator="$v.form.hide_premium_info_badges">
              <div class="setting-group-title">{{ T__("Other") }}</div>
              <label class="label-cb"
                ><input
                  type="checkbox"
                  true-value="1"
                  false-value="0"
                  v-model="form.hide_premium_info_badges"
                />

                Hide the
                <span class="pro-badge-mu">Premium feature</span> admin-end
                badge.</label
              ><span class="txt-help" style="margin-bottom:5px;"
                >This will hide the 'Premium feature' badge displayed in the
                plugin's admin pages. This is useful if you do not want your
                clients to see the badge.<br /><br />This is NOT for hiding the
                'Powered by WPCal.io' branding in the booking widget.</span
              >
              <!-- This is useful if you do not want your
                clients to see the badge during their Trial period. -->
            </form-row>

            <div class="wpc-form-row">
              <button
                type="button"
                class="wpc-btn primary lg mt20"
                @click="form_submit"
                style="width:100%"
                :disabled="!save_btn_is_enabled"
              >
                {{ T__("Save") }}
              </button>
            </div>
          </div>
          <AdminInfoLine info_slug="ownership_settings_general" />
        </div>
      </div>
    </div>
    <modal
      name="template_overrides_list_modal"
      :width="'800px'"
      height="auto"
      :scrollable="true"
      :adaptive="true"
      style="z-index:9999;"
    >
      <div class="modal_close_cont">
        <button @click="$modal.hide('template_overrides_list_modal')"></button>
      </div>
      <div class="mbox bg-light shadow mb0">
        <table width="100%" class="template_overrides_list">
          <tr>
            <th
              style="font-family: 'RubikMed'; font-weight: unset !important;"
              width="50%"
            >
              Default template
            </th>
            <th
              style="font-family: 'RubikMed'; font-weight: unset !important;"
              width="50%"
            >
              Overriding template
            </th>
          </tr>
          <tr v-if="_isEmpty(template_overrides)">
            <td colspan="2">No template has been overridden</td>
          </tr>
          <tr
            v-for="(overridden_template, template) in template_overrides"
            :key="template"
          >
            <td style="vertical-align:top:">{{ template }}</td>
            <td style="vertical-align:top:">{{ overridden_template }}</td>
          </tr>
        </table>
      </div>
    </modal>
    <!-- <v-dialog /> -->
  </div>
</template>

<script>
import SettingsMenu from "@/components/admin/SettingsMenu.vue";

import TimeRange1ToN from "@/components/form_elements/TimeRange1ToN.vue";
import WeekDaysSelector from "@/components/form_elements/WeekDaysSelector.vue";
import TimeZoneSelector from "@/components/form_elements/TimeZoneSelector.vue";

import { on_before_leave_alert_if_unsaved } from "@/utils/on_before_leave_alert_if_unsaved_mixin.js";

import { required } from "vuelidate/lib/validators";

import {
  in_array,
  subset,
  valid_time,
  min_time_as,
  valid_color
} from "@/utils/common_func.js";

import axios from "axios";

export default {
  mixins: [on_before_leave_alert_if_unsaved("saved_form", "form")],
  components: {
    SettingsMenu,
    TimeRange1ToN,
    WeekDaysSelector,
    TimeZoneSelector
  },
  data() {
    return {
      document_title: this.T__("General ‹ Settings"),
      working_hours_array_: [],
      save_btn_is_enabled: true,
      form: this.clone_deep(this.$store.getters.get_general_settings),
      template_overrides: {}

      // form: {
      //   working_hours: {
      //     from_time: "",
      //     to_time: ""
      //   },
      //   working_days: [],
      //   time_format: "24hrs"
      // }
    };
  },
  validations: {
    form: {
      working_hours: {
        from_time: { required, valid_time },
        to_time: { required, valid_time, min_time: min_time_as("from_time") }
      },
      working_days: { subset_working_days: subset([1, 2, 3, 4, 5, 6, 7]) },
      time_format: {
        required,
        time_format_in_array: in_array(["12hrs", "24hrs"])
      },
      branding_color: { valid_color },
      branding_font: { branding_font_in_array: in_array(["inherit", ""]) },
      use_wp_mail: { use_wp_mail_in_array: in_array(["1", "0"]) },
      hide_premium_info_badges: {
        hide_premium_info_badges_in_array: in_array(["1", "0"])
      }
    }
  },
  computed: {
    working_hours() {
      this.$set(this.working_hours_array_, 0, this.form.working_hours);
      return this.working_hours_array_;
    },
    brand_color_or_default() {
      return this.form.branding_color ? this.form.branding_color : "#567bf3";
    },
    saved_form() {
      return this.clone_deep(this.$store.getters.get_general_settings);
    }
    // form() {
    //   return this.clone_deep(this.$store.getters.get_general_settings);
    // }
  },
  methods: {
    load_page() {
      // let action = "get_general_settings";
      // let wpcal_request = {};
      // wpcal_request[action] = {
      //   dummy__: ""
      // };
      // let post_data = {
      //   action: "wpcal_process_admin_ajax_request",
      //   wpcal_request: wpcal_request
      // };
      // axios
      //   .post(window.wpcal_ajax.ajax_url, post_data)
      //   .then(response => {
      //     console.log(response);
      //     if (
      //       response.data &&
      //       response.data[action].hasOwnProperty("general_settings")
      //     ) {
      //       this.form = response.data[action].general_settings;
      //       console.log(this.form);
      //     }
      //   })
      //   .catch(error => {
      //     console.log(error);
      //   });
      //console.log(this.$store.getters.get_general_settings);
      //this.form = this.$store.getters.get_general_settings;
      //this.form = Object.assign({}, this.$store.getters.get_general_settings);
    },
    form_submit() {
      this.clear_validation_errors_cont();
      this.$v.form.$touch();
      if (this.$v.form.$anyError) {
        this.scroll_to_first_validation_error();
        return false;
      }

      this.save_btn_is_enabled = false;

      let action_update_general_settings = "update_general_settings";
      let wpcal_request = {};
      wpcal_request[action_update_general_settings] = {
        general_settings: this.form
      };

      let action_get_general_settings = "get_general_settings";
      wpcal_request[action_get_general_settings] = {
        dummy__: ""
      };

      let post_data = {
        action: "wpcal_process_admin_ajax_request",
        wpcal_request: wpcal_request
      };

      axios
        .post(window.wpcal_ajax.ajax_url, post_data)
        .then(response => {
          //console.log(response);
          this.notify_single_action_result(
            response.data[action_update_general_settings],
            { success_msg: this.T__("Settings saved.") }
          );

          if (
            response.data &&
            response.data[action_get_general_settings].hasOwnProperty(
              "general_settings"
            )
          ) {
            let general_settings =
              response.data[action_get_general_settings].general_settings;
            this.$store.commit("set_general_settings", general_settings);
          }
        })
        .catch(error => {
          console.log(error);
        })
        .then(() => {
          this.save_btn_is_enabled = true;
        });
    },
    may_prefix_with_hash() {
      let color = this.form.branding_color;
      color = color.trim();
      if (color.length && color.charAt(0) !== "#") {
        color = "#" + color;
        this.form.branding_color = color;
      }
    },
    may_alert_already_scheduled_mails() {
      if (this.form.use_wp_mail == this.saved_form.use_wp_mail) {
        return;
      }
      this.$modal.show("dialog", {
        title: this.T__("Heads up!"),
        text: this.T__(
          "The emails which are already scheduled will go as per old setting."
        ),
        buttons: [
          {
            title: this.T__("Okay"),
            default: true // Will be triggered by default if 'Enter' pressed.
          }
        ]
      });
    },
    load_and_show_template_overrides() {
      let action = "get_template_overrides";
      let wpcal_request = {};
      wpcal_request[action] = {
        dummy__: ""
      };
      let post_data = {
        action: "wpcal_process_admin_ajax_request",
        wpcal_request: wpcal_request
      };
      axios
        .post(window.wpcal_ajax.ajax_url, post_data)
        .then(response => {
          if (
            response.data &&
            response.data[action].hasOwnProperty("template_overrides")
          ) {
            this.template_overrides = response.data[action].template_overrides;
          }
        })
        .catch(error => {
          console.log(error);
        })
        .then(() => {
          this.show_template_overrides_modal();
        });
    },
    show_template_overrides_modal() {
      this.$modal.show("template_overrides_list_modal");
    }
  },
  watch: {
    "$store.getters.get_general_settings": function() {
      this.form = this.clone_deep(this.$store.getters.get_general_settings);
      this.$v.form.$reset();
    }
  },
  created() {
    this.load_page();
  }
};
</script>
<style>
.pro-badge-mu {
  background-color: #f9f5bf;
  position: relative;
  color: #e19334;
  border: 1px dashed #e19334;
  padding: 2px 3px 1px 15px;
  border-radius: 5px;
  text-transform: uppercase;
  font-size: 12px;
  font-family: "RubikMed" !important;
  margin: 0 2px;
}
.pro-badge-mu::before {
  content: "";
  position: absolute;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 99.17 99.17'%3E%3Cdefs%3E%3Cstyle%3E.cls-1%7Bfill:%23e19334;%7D%3C/style%3E%3C/defs%3E%3Cg id='Layer_2' data-name='Layer 2'%3E%3Cg id='Layer_1-2' data-name='Layer 1'%3E%3Cpath class='cls-1' d='M49.58,0A49.59,49.59,0,1,0,99.17,49.58,49.58,49.58,0,0,0,49.58,0ZM74.5,47.9l-7.58,5.32a1.47,1.47,0,0,0-.5,1.78l4.19,9.6a5.82,5.82,0,0,1-1.47,6.75,6,6,0,0,1-7,.78L50.73,65.71a1.51,1.51,0,0,0-1.43,0L37.92,72.13a6,6,0,0,1-7-.78,5.82,5.82,0,0,1-1.47-6.75L33.63,55a1.47,1.47,0,0,0-.5-1.75l-7.19-5a6,6,0,0,1-1.75-6.73,5.74,5.74,0,0,1,5.42-3.59h8.65a1.46,1.46,0,0,0,1.36-1l4.93-13a6.15,6.15,0,0,1,10.83-.29c0,.1.09.19.13.29l4.92,13a1.49,1.49,0,0,0,1.37,1h8.64a5.73,5.73,0,0,1,5.45,3.57A5.82,5.82,0,0,1,74.5,47.9Z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  width: 10px;
  height: 10px;
  margin-top: 2px;
  left: 3px;
}
table.template_overrides_list {
  border-collapse: collapse;
  background-color: #fff;
}
table.template_overrides_list td,
table.template_overrides_list th {
  border: 1px solid #b1b6d1;
  padding: 10px;
}
table.template_overrides_list td {
  word-break: break-all;
}
</style>
